import React, { useEffect, useRef, useState } from 'react';
import { TriangleAnimation } from './TriangleAnimation';
import toolsTriangle from '../../../source/images/homepage/tools-triangle.png';
import costTriangle from '../../../source/images/homepage/cost-triangle.png';
import builtTriangle from '../../../source/images/homepage/built-triangle.png';

export default function ProblemSection() {
  const sectionsRef = useRef(null);
  const stickyRef = useRef(null);

  const [inView, setInView] = useState(false);
  const [section, setSection] = useState('empty');

  const handleScroll = () => {
    const rect = sectionsRef.current?.getBoundingClientRect();

    const viewportHeight = window.innerHeight;

    const visibleTop = Math.max(rect.top, 0);
    const visibleBottom = Math.min(rect.bottom, viewportHeight);

    const visibleHeight = visibleBottom - visibleTop;

    if (visibleHeight >= 300) {
      setInView(true);

      const stickyRect = stickyRef.current.getBoundingClientRect();
      const sectionHeight = rect.height / 3;

      const offset = stickyRect.top - rect.top;

      if (offset < sectionHeight / 2) {
        setSection('icons');
      } else if (offset < sectionHeight * 1.5) {
        setSection('pills');
      } else {
        setSection('logo');
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call initially

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="flex flex-col items-center relative bg-skyberry-03 py-[100px] sm:pt-[120px] sm:pb-[144px] px-6 sm:px-12">
      <div
        className="flex flex-col gap-6 md:gap-10 lg:gap-14 items-start w-full h-full"
        ref={sectionsRef}
      >
        {sectionContent.map((section) => (
          <Section section={section} key={section.heading} />
        ))}

        <div className="animation-container h-[calc(100%-264px)] lg:h-[calc(100%-200px)] absolute hidden md:block sm:right-12 top-[120px] lg:top-[100px] bottom-[144px] lg:bottom-[100px]">
          <div
            className="animation sticky right-0 top-[calc(50vh-200px)] w-[300px] h-[300px] lg:w-[400px] lg:h-[400px]"
            ref={stickyRef}
          >
            <TriangleAnimation inView={inView} sectionSelection={section} />
          </div>
        </div>
      </div>
    </section>
  );
}

const Section = ({ section }) => (
  <div className="flex-1 flex flex-col sm:w-full md:w-[50%] md:flex-row justify-between items-center gap-8 max-w-[1120px] mb-[60px] lg:mb-6">
    <div className="relative sm:max-w-[50%] md:max-w-[464px] w-full">
      <div>
        <div className="badge-skyberry inline-block mb-6">{section.badge}</div>
        <h2 className="h2 mb-2">{section.heading}</h2>
        <p className="body-text --light-64">{section.content}</p>
      </div>
    </div>
    <div className="flex-1 max-w-[800px] w-full md:hidden">
      <div className="aspect-w-[800] aspect-h-[600]">
        <img
          src={section.image}
          alt=""
          className="object-contain object-center"
        />
      </div>
    </div>
  </div>
);

const sectionContent = [
  {
    badge: 'The Problem',
    heading: 'The HR, Finance, and Talent Gap',
    content:
      'HR, Finance, and Talent teams rely on different tools and systems, yet they must stay aligned on strategy, planning, and growth. Keeping everyone in sync is time-consuming, and maintaining data accuracy is a constant challenge—especially when spreadsheets, emails, and manual workarounds are the only bridge between them.',
    image: toolsTriangle,
  },
  {
    badge: 'The Cost',
    heading: 'The Mess in the Middle',
    content:
      'When HR, Finance, and Talent teams rely on complex spreadsheets and manual workarounds, it creates a ‘mess in the middle’—wasting time, mismanaging data, inflating costs, and ultimately harming talent retention and engagement.',
    image: costTriangle,
  },
  {
    badge: 'The Solution',
    heading: 'Bridging the Gap',
    content:
      'Built eliminates the complexity by providing a single, integrated source of truth for all position data and workflows. Our position-centric approach streamlines org design, headcount planning, and succession planning—delivering faster execution, greater accuracy, and seamless alignment across the organization. The result? Smarter decisions and stronger strategic outcomes.',
    image: builtTriangle,
  },
];
